import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { History, CheckCircle, Download, FileText, Mic, RefreshCw, Calendar, Search, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import { downloadAudioDirectly } from '../lib/offlineSurveyQueue'

type UserProfile = 'admin' | 'researcher'

interface AppSession {
  profile: UserProfile
  userId: string
  email: string
  userName: string
  userRole?: string
  researcherId?: string | null
  researcherName?: string | null
  loginAt: string
}

interface SurveyResponse {
  _id: string
  surveyId: string
  researcherId: string
  status: string
  surveyTitle?: string
  submittedAt?: string
  createdAt?: string
  audio_url?: string
  audioUrl?: string
  completedAt?: string
  isDeleted?: boolean
  deletedAt?: string
}

interface Survey {
  _id: string
  title: string
}

interface EnrichedResponse extends SurveyResponse {
  survey?: Survey
}

const SESSION_KEY = 'app_dna_session'

const readSession = (): AppSession | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const session = JSON.parse(raw) as AppSession
    const isValidProfile =
      session.profile === 'admin' || session.profile === 'researcher'
    if (!isValidProfile || !session.userId || !session.email || !session.userName) {
      return null
    }
    return session
  } catch {
    return null
  }
}

const formatDate = (iso?: string) => {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const stripHtml = (html: string) => {
  const tmp = document.createElement('div')
  tmp.innerHTML = html || ''
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim()
}

const ResearcherHistory: React.FC = () => {
  const navigate = useNavigate()
  const session = readSession()

  const [responses, setResponses] = useState<EnrichedResponse[]>([])
  const [filtered, setFiltered] = useState<EnrichedResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [audioPlaying, setAudioPlaying] = useState<string | null>(null)

  useEffect(() => {
    if (!session || session.profile !== 'researcher' || !session.researcherId) {
      navigate('/login', { replace: true })
    }
  }, [])

  const fetchHistory = async () => {
    if (!session?.researcherId) return
    setLoading(true)
    try {
      const { list: responseList } = (await lumi.entities.responses.list({
        filter: { researcherId: session.researcherId },
        sort: { submittedAt: -1, createdAt: -1 },
      })) as { list: SurveyResponse[] }

      const visibleResponses = (responseList || []).filter((response) => !response.isDeleted && !response.deletedAt)

      if (visibleResponses.length === 0) {
        setResponses([])
        setFiltered([])
        return
      }

      const surveyIds = [...new Set(visibleResponses.map((response) => response.surveyId).filter(Boolean))]
      const surveyResults = await Promise.all(
        surveyIds.map((id) =>
          lumi.entities.surveys
            .list({ filter: { _id: id }, limit: 1 })
            .catch(() => ({ list: [] }))
        )
      )

      const surveyMap: Record<string, Survey> = {}
      surveyResults.forEach((res: any) => {
        const survey = res?.list?.[0]
        if (survey) surveyMap[survey._id] = survey
      })

      const enriched: EnrichedResponse[] = visibleResponses.map((response) => ({
        ...response,
        survey: surveyMap[response.surveyId],
      }))

      setResponses(enriched)
      setFiltered(enriched)
    } catch (err) {
      console.error('Erro ao carregar histórico:', err)
      toast.error('Erro ao carregar histórico de respostas.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHistory()
  }, [])

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(responses)
      return
    }

    const query = search.toLowerCase()
    setFiltered(
      responses.filter((response) => {
        const title = response.survey?.title || response.surveyTitle || ''
        return stripHtml(title).toLowerCase().includes(query)
      })
    )
  }, [search, responses])

  const [historyPage, setHistoryPage] = useState(1)
  const historyTotalPages = Math.max(1, Math.ceil(filtered.length / 10))
  const historyCurrentPage = Math.min(historyPage, historyTotalPages)
  const historyPageItems = filtered.slice((historyCurrentPage - 1) * 10, historyCurrentPage * 10)

  const resolveTitle = (response: EnrichedResponse) => {
    const raw = response.survey?.title || response.surveyTitle || ''
    return raw ? stripHtml(raw) : `Pesquisa #${response.surveyId?.slice(-6) || response._id.slice(-6)}`
  }

  const resolveAudioUrl = (response: EnrichedResponse): string | undefined =>
    response.audio_url || response.audioUrl || undefined

  const handleDownloadMp3 = async (url: string, id: string, response?: EnrichedResponse) => {
    const fileName = `audio_${id.slice(-8)}.mp3`
    try {
      await downloadAudioDirectly(url, fileName, {
        responseId: id,
        audioData: (response as any)?.audio_data,
      })
      toast.success('Download do áudio (.mp3) iniciado.')
    } catch (err) {
      console.error('Erro ao baixar áudio:', err)
      toast.error('Não foi possível baixar o áudio diretamente.')
    }
  }

  const resolveDate = (response: EnrichedResponse) =>
    response.submittedAt || response.completedAt || response.createdAt

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History size={24} className="text-blue-600" />
            Histórico de Respostas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Todas as respostas enviadas por você.
          </p>
        </div>
        <button
          onClick={fetchHistory}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: 'Total de Respostas',
            value: responses.length,
            color: 'text-blue-600',
            bg: 'bg-blue-50',
            icon: FileText,
          },
          {
            label: 'Com Áudio',
            value: responses.filter((response) => !!resolveAudioUrl(response)).length,
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            icon: Mic,
          },
          {
            label: 'Concluídas',
            value: responses.filter(
              (response) => response.status === 'completed' || !!response.submittedAt
            ).length,
            color: 'text-green-600',
            bg: 'bg-green-50',
            icon: CheckCircle,
          },
        ].map((stat) => {
          const Icon = stat.icon
          return (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3"
            >
              <div className={`${stat.bg} p-2 rounded-lg`}>
                <Icon size={20} className={stat.color} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          type="text"
          placeholder="Buscar por título da pesquisa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">Respostas Enviadas</h2>
          {!loading && (
            <span className="text-xs text-gray-400">
              {filtered.length} {filtered.length === 1 ? 'registro' : 'registros'}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw size={24} className="animate-spin text-blue-500 mr-3" />
            <span className="text-gray-500">Carregando histórico...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <History size={40} className="text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">
              {search ? 'Nenhuma resposta encontrada' : 'Nenhuma resposta enviada ainda'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {search
                ? 'Tente outro termo de busca.'
                : 'Suas respostas enviadas aparecerão aqui após a coleta.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {historyPageItems.map((response) => {
              const title = resolveTitle(response)
              const date = resolveDate(response)
              const hasAudio = !!resolveAudioUrl(response)
              const audioSrc = resolveAudioUrl(response)

              return (
                <li
                  key={response._id}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle size={11} />
                          Concluída
                        </span>
                        {hasAudio && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <Mic size={11} />
                            Com áudio
                          </span>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar size={11} />
                          Enviada em: {formatDate(date)}
                        </span>
                        <span className="text-gray-300">ID: {response._id.slice(-8)}</span>
                      </div>
                    </div>

                    {hasAudio && (
                      <div className="shrink-0 flex items-center gap-2">
                        {audioPlaying === response._id ? (
                          <div className="flex flex-col items-end gap-1">
                            <audio
                              autoPlay
                              controls
                              src={audioSrc}
                              onEnded={() => setAudioPlaying(null)}
                              className="h-8 w-48"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadMp3(audioSrc, response._id, response)}
                                className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800"
                              >
                                <Download size={11} />
                                Baixar .mp3
                              </button>
                              <button
                                onClick={() => setAudioPlaying(null)}
                                className="text-xs text-gray-400 hover:text-gray-600"
                              >
                                Fechar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => setAudioPlaying(response._id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                            >
                              <Mic size={13} />
                              Ouvir áudio
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadMp3(audioSrc, response._id, response)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Baixar áudio em formato .mp3"
                            >
                              <Download size={13} />
                              .mp3
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {filtered.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {historyCurrentPage} de {historyTotalPages} · {filtered.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={historyCurrentPage <= 1}
                onClick={() => setHistoryPage(historyCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={historyCurrentPage >= historyTotalPages}
                onClick={() => setHistoryPage(historyCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ResearcherHistory

